import { formatDate } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";
import { EmailRecord } from "@models/email-templates/email-record";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { FilterOption } from "@models/grid/grid-filter";
import { EmailStatus } from "@models/outgoing-messages/email-status.enum";
import { PageResult } from "@models/requests/page-result.interface";
import {
  AgEmailRecipientsDropdownComponent
} from "@pages/outgoing-messages/components/outgoing-email-messages-page/components/ag-email-recipients-dropdown/ag-email-recipients-dropdown.component";
import {
  AgEmailSubjectComponent
} from "@pages/outgoing-messages/components/outgoing-email-messages-page/components/ag-email-subject/ag-email-subject.component";
import {
  AgOutgoingEmailMessageStatusComponent
} from "@pages/outgoing-messages/components/outgoing-email-messages-page/components/ag-outgoing-message-status/ag-outgoing-email-message-status.component";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { dateTimeComparator } from "@shared/utils/grid/date-time-comparator";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";
import { ColDef, IServerSideDatasource, IServerSideGetRowsParams, ValueFormatterParams } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";

@Injectable()
export class OutgoingEmailsGridService extends BaseGridService {
  private emailTemplatesService = inject(EmailsApiService);
  private toastr = inject(ToastrService);

  totalRecords = signal<number>(0);
  dataSource = signal<IServerSideDatasource>(this.getDataSource());

  dataLoaded$ = new Subject<PageResult<EmailRecord>>();

  // filters
  readonly emailMessagesStatusFilterOptions = convertEnumToFilterOption(EmailStatus);

  profileUserEmailsList: string[] = [];
  sendersFilterOptions: FilterOption[] = [];

  setGridFilterOptions(
    profileUserEmailsList: string[],
  ): void {
    this.profileUserEmailsList = profileUserEmailsList;
    this.sendersFilterOptions = uniqueArrayToFilterOptionHelper(this.profileUserEmailsList);
  }

  setDataSource(searchText?: string, endRow?: number): void {
    this.dataSource.set(this.getDataSource(searchText, endRow));
  }

  getDataSource(searchText = '', endRow?: number): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const request = params.request;

        if (endRow) {
          request.endRow = endRow;
        }

        this.emailTemplatesService.getOutgoingEmailMessagesList(request, searchText)
          .subscribe({
            next: (result) => {
              this.totalRecords.set(result.total);

              params.success({
                rowData: result.records,
                rowCount: result.total,
              });

              this.dataLoaded$.next(result);
            },
            error: (err) => {
              this.toastr.error('Something happened while loading outgoing email messages', 'Error');
              console.error(err);
              params.fail();
            }
          });
      }
    };
  }

  setColDefsWithCellClickHandler(actionColumn: ColDef, cellClickHandler: Function) {
    const colDefs = [
      {
        headerName: 'Date',
        field: 'createdOn',
        sort: 'desc',
        width: 200,
        minWidth: 200,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateTimeComparator,
        },
        cellDataType: CellDataType.DATE,
        valueFormatter: (params: ValueFormatterParams<EmailRecord>) => {
          return formatDate(params.data?.createdOn ?? '', 'dd MMM yyyy HH:mm', 'en-US');
        },
        onCellClicked: cellClickHandler.bind(this),
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Subject',
        field: 'subject',
        cellRenderer: AgEmailSubjectComponent,
        flex: 1,
        onCellClicked: cellClickHandler.bind(this),
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Recipients',
        field: 'to',
        cellRenderer: AgEmailRecipientsDropdownComponent,
        flex: 2,
        onCellClicked: cellClickHandler.bind(this),
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: AgOutgoingEmailMessageStatusComponent,
        width: 105,
        minWidth: 105,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.emailMessagesStatusFilterOptions,
          values: Object.values(EmailStatus).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: ['generalMenuTab']
      },
      {
        headerName: 'Sender',
        field: 'from',
        flex: 1,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.sendersFilterOptions,
          values: this.profileUserEmailsList,
        },
        onCellClicked: cellClickHandler.bind(this),
        menuTabs: ['generalMenuTab'],
        cellClass: 'hover-underline'
      },
      actionColumn
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));

    this.colDefs.set(colDefs);
  }
}
